import DOMPurify from 'dompurify';
import { Extension, type Editor } from '@tiptap/core';
import { closeHistory } from '@tiptap/pm/history';
import { Plugin, PluginKey, type EditorState, type Transaction } from '@tiptap/pm/state';
import { Decoration, DecorationSet } from '@tiptap/pm/view';

import { DOCUMENT_HTML_ATTRS, DOCUMENT_HTML_TAGS } from '@modules/documents/largeDocument/static/sanitizeElementHtml';

import { undoNoScroll } from '@tiptap/pm/history';
import { isSystemTr, markSystemTr } from './shared';
import { SECTION_HTML_SELECTOR } from '../nodes/section/constants';
import { hoistWrappedFragment, type TTableFragmentTag } from '@confinion/confideck-shared';

const AI_EDIT_REVIEW_META = 'aiEditReview';

// WARNING Mirrored in FE/BE - keep values in sync.
// Counterpart: backend/src/ai/useCases/edit/types/edit.types.ts
export enum EAiEditSuggestionKind {
  REPLACE = 'REPLACE',
  INSERT_BEFORE = 'INSERT_BEFORE',
  INSERT_AFTER = 'INSERT_AFTER',
  REMOVE = 'REMOVE',
}

export type TAiEditRange = { pos: number; end: number };

export type TAiEditPreview = {
  suggestions: Array<{
    id: string;
    /** For an insertion next to an inserted node, the saved node the chain starts at. */
    savedElementId: string;
    placement: EAiEditSuggestionKind;
    target: TAiEditRange;
    html?: string;
    /** Set when a div is invalid where the widget sits: inside a table row or body, or inside a paragraph. */
    widgetTag?: TTableFragmentTag | 'span';
  }>;
};

type TAiEditPreviewWidgetPlacement = Exclude<
  TAiEditPreview['suggestions'][number]['placement'],
  EAiEditSuggestionKind.REMOVE
>;

type TAiEditReviewAction =
  | { type: 'BEGIN' }
  | { type: 'PREVIEW'; preview: TAiEditPreview }
  | { type: 'APPLY' }
  | { type: 'FOCUS'; elementId?: string }
  | { type: 'END' };

/**
 * Booleans, not ids: only one AI edit run can exist per document - the document lock owns that - so
 * there is never a second review to tell this one apart from.
 */
type TAiEditReviewState = {
  isActive: boolean;
  preview?: TAiEditPreview;
  focusedElementId?: string;
  isRevertable: boolean;
  /** A proposed landscape section widens the page, so the layout has to know before it is accepted. */
  previewHasLandscapeSection: boolean;
};

const aiEditReviewPluginKey = new PluginKey<TAiEditReviewState>('ai-edit-review');

declare module '@tiptap/core' {
  interface Commands<ReturnType> {
    aiEditReview: {
      beginAiEditReview: () => ReturnType;
      showAiEditPreview: (preview: TAiEditPreview) => ReturnType;
      focusAiEditChange: (elementId?: string) => ReturnType;
      applyAiEditTransaction: (transaction: Transaction) => ReturnType;
      endAiEditReview: () => ReturnType;
      revertAiEdit: () => ReturnType;
    };
  }
}

/** A widget element's own id, kept apart from `id` and `data-id` so it never passes for a live node. */
export const AI_EDIT_PREVIEW_ID_ATTRIBUTE = 'data-ai-edit-preview-id';

export const AI_EDIT_PREVIEW_WIDGET_CLASS = 'ai-edit-preview-widget';

/** Prevents untrusted preview markup from executing or impersonating live document nodes. */
const SANITIZE_CONFIG = {
  ADD_TAGS: [...DOCUMENT_HTML_TAGS],
  ADD_ATTR: [...DOCUMENT_HTML_ATTRS],
  FORBID_ATTR: ['id', 'data-id'],
};

const sanitizeReviewPreviewHtml = (html: string): string => {
  const template = document.createElement('template');
  template.innerHTML = html;
  template.content.querySelectorAll('[data-id], [id]').forEach((element) => {
    element.setAttribute(AI_EDIT_PREVIEW_ID_ATTRIBUTE, element.getAttribute('data-id') ?? element.id);
  });
  return DOMPurify.sanitize(template.innerHTML, SANITIZE_CONFIG);
};

/** Read off the sanitized markup the widget will show, not the raw suggestion, so both agree. */
const hasLandscapeSection = (preview: TAiEditPreview): boolean =>
  preview.suggestions.some((suggestion) => {
    if (!suggestion.html) return false;
    const holder = document.createElement('div');
    holder.innerHTML = sanitizeReviewPreviewHtml(suggestion.html);
    return holder.querySelector(SECTION_HTML_SELECTOR) !== null;
  });

const createPreviewWidget = (
  elementId: string,
  html: string,
  focused: boolean,
  tag: TTableFragmentTag | 'span' | 'div' = 'div'
): HTMLElement => {
  const sanitized = sanitizeReviewPreviewHtml(html);
  const holder = document.createElement('div');
  holder.innerHTML = sanitized;
  // A ticked task item diffs into two items - old and new - and one widget cannot BE both.
  const asTaskList = tag === 'li' && holder.querySelectorAll('li[data-type="taskItem"]').length > 1;
  const widget = document.createElement(asTaskList ? 'ul' : tag);
  widget.className = AI_EDIT_PREVIEW_WIDGET_CLASS;
  widget.contentEditable = 'false';
  widget.dataset.aiEditElementId = elementId;
  if (focused) widget.classList.add('ai-edit-preview-focused');
  widget.innerHTML = sanitized;
  if (asTaskList) {
    widget.dataset.type = 'taskList';
    widget.replaceChildren(...Array.from(widget.querySelector('ul')?.childNodes ?? []));
  } else if (tag !== 'div' && tag !== 'span') {
    // The html arrives table-wrapped so its cell survives parsing; the widget IS the cell (or row).
    hoistWrappedFragment(widget, tag);
  }
  return widget;
};

export const __testCreatePreviewWidget = createPreviewWidget;

const getPreviewWidgetSide = (
  placement: TAiEditPreviewWidgetPlacement,
  index: number,
  suggestionCount: number
): number => {
  if (placement === EAiEditSuggestionKind.INSERT_AFTER) return index - suggestionCount;
  if (placement === EAiEditSuggestionKind.REPLACE) return suggestionCount + index;
  return index;
};

const buildDecorations = (state: EditorState, reviewState?: TAiEditReviewState): DecorationSet => {
  const preview = reviewState?.preview;
  if (!preview) return DecorationSet.empty;

  const decorations: Decoration[] = [];
  preview.suggestions.forEach((suggestion, index) => {
    const { pos, end } = suggestion.target;
    const { placement } = suggestion;

    if (placement === EAiEditSuggestionKind.REMOVE) {
      decorations.push(
        Decoration.node(pos, end, {
          class: `diff-del ai-edit-preview-delete${
            reviewState.focusedElementId === suggestion.savedElementId ? ' ai-edit-preview-focused' : ''
          }`,
          'data-ai-edit-element-id': suggestion.savedElementId,
        })
      );
      return;
    }

    if (!suggestion.html) return;
    if (placement === EAiEditSuggestionKind.REPLACE) {
      decorations.push(
        Decoration.node(pos, end, {
          class: 'ai-edit-preview-source-hidden',
          style: 'display: none',
        })
      );
    }

    const isAfter = placement === EAiEditSuggestionKind.INSERT_AFTER;
    const widgetPos = isAfter ? end : pos;
    decorations.push(
      Decoration.widget(
        widgetPos,
        () =>
          createPreviewWidget(
            suggestion.savedElementId,
            suggestion.html!,
            reviewState.focusedElementId === suggestion.savedElementId,
            suggestion.widgetTag
          ),
        {
          key: `${suggestion.id}:${reviewState.focusedElementId === suggestion.savedElementId}`,
          // At a shared boundary: previous-target after, current-target before, then replacement.
          // The preview lists suggestions in rendered order, so the index keeps order inside each band.
          side: getPreviewWidgetSide(placement, index, preview.suggestions.length),
        }
      )
    );
  });

  return decorations.length ? DecorationSet.create(state.doc, decorations) : DecorationSet.empty;
};

const applyAction = (
  value: TAiEditReviewState,
  action: TAiEditReviewAction | undefined,
  transaction: Transaction
): TAiEditReviewState => {
  let next = value;
  if (action?.type === 'BEGIN') next = { ...value, isActive: true };
  if (action?.type === 'PREVIEW' && value.isActive) {
    next = { ...value, preview: action.preview, previewHasLandscapeSection: hasLandscapeSection(action.preview) };
  }
  if (action?.type === 'FOCUS' && value.isActive) next = { ...value, focusedElementId: action.elementId };
  if (action?.type === 'END' && value.isActive) {
    next = { isActive: false, isRevertable: value.isRevertable, previewHasLandscapeSection: false };
  }

  if (transaction.docChanged && action?.type === 'APPLY' && value.isActive) {
    // The preview dies in the SAME transaction that applies the change, or the applied node and
    // its preview copy are both on screen for a frame.
    next = { ...next, preview: undefined, previewHasLandscapeSection: false, isRevertable: true };
  } else if (transaction.docChanged && !isSystemTr(transaction)) {
    next = { ...next, isRevertable: false };
  }
  return next;
};

export const isAiEditRevertable = (state: EditorState): boolean =>
  Boolean(aiEditReviewPluginKey.getState(state)?.isRevertable);

export const isAiEditReviewActive = (state: EditorState): boolean =>
  Boolean(aiEditReviewPluginKey.getState(state)?.isActive);

export const hasAiEditPreviewLandscapeSection = (state: EditorState): boolean =>
  Boolean(aiEditReviewPluginKey.getState(state)?.previewHasLandscapeSection);

const AiEditReviewPlugin = Extension.create({
  name: 'aiEditReview',

  addCommands() {
    return {
      beginAiEditReview:
        () =>
        ({ state, tr, dispatch }) => {
          if (isAiEditReviewActive(state)) return false;
          if (dispatch) tr.setMeta(AI_EDIT_REVIEW_META, { type: 'BEGIN' } satisfies TAiEditReviewAction);
          return true;
        },
      showAiEditPreview:
        (preview: TAiEditPreview) =>
        ({ state, tr, dispatch }) => {
          if (!isAiEditReviewActive(state)) return false;
          if (dispatch) tr.setMeta(AI_EDIT_REVIEW_META, { type: 'PREVIEW', preview } satisfies TAiEditReviewAction);
          return true;
        },
      focusAiEditChange:
        (elementId?: string) =>
        ({ state, tr, dispatch }) => {
          if (!isAiEditReviewActive(state)) return false;
          if (dispatch) {
            tr.setMeta(AI_EDIT_REVIEW_META, { type: 'FOCUS', elementId } satisfies TAiEditReviewAction);
          }
          return true;
        },
      applyAiEditTransaction:
        (transaction: Transaction) =>
        ({ editor, state, tr, dispatch }) => {
          if (!isAiEditReviewActive(state) || transaction.before !== editor.state.doc) return false;
          if (!dispatch) return true;

          // This command dispatches several ordered transactions, so prevent TipTap from dispatching its stale wrapper.
          tr.setMeta('preventDispatch', true);
          editor.view.dispatch(closeHistory(editor.state.tr));
          editor.view.dispatch(
            markSystemTr(transaction.setMeta(AI_EDIT_REVIEW_META, { type: 'APPLY' } satisfies TAiEditReviewAction), {
              keepInHistory: true,
            })
          );
          editor.view.dispatch(closeHistory(editor.state.tr));
          return editor.state.doc !== transaction.before;
        },
      endAiEditReview:
        () =>
        ({ state, tr, dispatch }) => {
          if (!isAiEditReviewActive(state)) return false;
          if (dispatch) tr.setMeta(AI_EDIT_REVIEW_META, { type: 'END' } satisfies TAiEditReviewAction);
          return true;
        },
      revertAiEdit:
        () =>
        ({ editor, state, tr, dispatch }) => {
          if (isAiEditReviewActive(state) || !isAiEditRevertable(state)) return false;
          // Plain undo scrolls its restored selection into view - the document top, not the
          // content being reverted, which is what the user is looking at.
          if (!dispatch) return undoNoScroll(state);

          // Dispatched here, not by TipTap after the return, so the result can be read: the history
          // answers true even when a filter - the template lock - vetoes the undo.
          tr.setMeta('preventDispatch', true);
          const before = editor.state.doc;
          undoNoScroll(editor.state, (undo) => editor.view.dispatch(undo));
          return editor.state.doc !== before;
        },
    };
  },

  addProseMirrorPlugins() {
    return [
      new Plugin<TAiEditReviewState>({
        key: aiEditReviewPluginKey,
        state: {
          init: () => ({ isActive: false, isRevertable: false, previewHasLandscapeSection: false }),
          apply: (transaction, value) =>
            applyAction(
              value,
              transaction.getMeta(AI_EDIT_REVIEW_META) as TAiEditReviewAction | undefined,
              transaction
            ),
        },
        props: {
          decorations(state) {
            return buildDecorations(state, aiEditReviewPluginKey.getState(state));
          },
        },
      }),
    ];
  },
});

export default AiEditReviewPlugin;
